import React, { useCallback, useEffect, useState } from "react";
import { notify } from "../../../../services/toast.config";
import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import CommonSelect from "../../../../core/common/commonSelect";

interface EditCategoryProps {
  selectedCompany: {
    company_id: string;
    company_name: string;
    company_type: string;
  };
  selectedCategory: any;
  updateUI: () => Promise<void>;
}

type CategoryFormData = {
  parent_category_id: string | any;
  id: string | any;
  name: string | any;
  status: string | any;
  company_id: string | any;
  company_type: string | any;
  company_name: string | any;
};

const DEFAULT_FORM_DATA: CategoryFormData = {
  parent_category_id: null,
  id: null,
  name: "",
  status: "",
  company_id: "",
  company_type: "",
  company_name: "",
};

const STATUS_OPTIONS: any = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const EditCategory: React.FC<EditCategoryProps> = ({
  selectedCompany,
  updateUI,
  selectedCategory,
}) => {
  // Local Storage
  const signature = localStorage.getItem("signature");

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);

  // Helper Functions
  const resetForm = useCallback(() => {
    if (selectedCategory && selectedCompany) {
      const { company_id, company_name, company_type } = selectedCompany;
      const { id, parentId, name, status } = selectedCategory;

      setFormData({
        parent_category_id: parentId || null,
        id: id || null,
        name: name || "",
        status: status || "",
        company_id: company_id || "",
        company_name: company_name || "",
        company_type: company_type || "",
      });
      setInvalidFields(new Set());
    }
  }, [selectedCompany, selectedCategory]);

  const validateForm = useCallback((): boolean => {
    const requiredFields = [
      { key: "id", label: "Category Id" },
      { key: "name", label: "Category Name" },
      { key: "status", label: "Status" },
      { key: "company_id", label: "Company" },
    ];

    const invalid = new Set<string>();
    requiredFields.forEach(({ key }) => {
      if (!formData[key as keyof CategoryFormData]?.toString().trim()) {
        invalid.add(key);
      }
    });

    setInvalidFields(invalid);
    return invalid.size === 0;
  }, [formData]);

  const clearFieldError = useCallback((fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  }, []);

  // Event Handlers
  const handleInputChange = useCallback(
    (key: string, value: string) => {
      clearFieldError(key);
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    [clearFieldError]
  );

  const handleSubmitCategory = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      notify.error("Please fill all required fields");
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);

      const dataToSend = {
        signature,
        id: formData.id,
        name: formData.name.trim(),
        status: formData.status.toLowerCase() === "active" ? 1 : 0,
        company_id: formData.company_id,
        company_type: formData.company_type,
        parent_category_id: formData.parent_category_id,
      };

      const response = await api.post("userService/updateCategory", dataToSend);
      notify.success(
        response?.data?.message || "Category updated successfully"
      );
      // Close modal on success
      const dismissButton = document.querySelector(
        "#edit-category-dismiss-modal-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }

      await updateUI();
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Internal Server Error!";
      }
      notify.error(errorMessage || "Internal Server Error!");
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Initialize form data whenever selectedCategory changes
  useEffect(() => {
    if (selectedCategory) {
      resetForm();
    }
  }, [selectedCategory, resetForm]);

  return (
    <>
      {isLoading && <Loader />}

      <div className="modal fade" id="edit_asset_category">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4
                className="modal-title"
                title={`Editing: ${formData.name || "Category"}`}>
                {formData.parent_category_id
                  ? "Edit Sub Category"
                  : "Edit Category"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmitting}
                title="Close modal">
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmitCategory}>
              <div className="modal-body pb-0">
                <div className="row">
                  {/* Company Selection */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Company <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${formData.company_name || ""} - ${
                        formData.company_type || ""
                      }`}
                      disabled={true}
                      readOnly
                      title={`Company: ${formData.company_name || ""} - ${
                        formData.company_type || ""
                      }`}
                    />
                    {invalidFields.has("company_id") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Company is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Category Name */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      {formData.parent_category_id
                        ? "Sub Category Name"
                        : "Category Name"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        invalidFields.has("name")
                          ? "is-invalid border-danger"
                          : ""
                      }`}
                      value={formData.name || ""}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder={`Enter ${
                        formData.parent_category_id
                          ? "sub category"
                          : "category"
                      } name`}
                      disabled={isSubmitting}
                      autoFocus
                      title={`Current name: ${formData.name || "Not set"}`}
                      maxLength={100}
                    />
                    {invalidFields.has("name") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          {formData.parent_category_id
                            ? "Sub Category"
                            : "Category"}{" "}
                          Name is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFields.has("status")
                          ? "border border-danger rounded"
                          : ""
                      }
                      title={`Current status: ${
                        formData.status || "Not selected"
                      }`}>
                      <CommonSelect
                        key={`status-${formData.company_id}-${Date.now()}`}
                        options={STATUS_OPTIONS}
                        defaultValue={
                          formData.status
                            ? { value: formData.status, label: formData.status }
                            : undefined
                        }
                        onChange={(selected: any) =>
                          handleInputChange("status", selected?.value || "")
                        }
                        placeHolder="Select Status"
                        isDisabled={isSubmitting}
                      />
                    </div>
                    {invalidFields.has("status") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Status is required
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  disabled={isSubmitting}
                  title="Cancel without saving changes">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || invalidFields.size > 0}
                  title={`Save changes to ${
                    formData.parent_category_id ? "sub category" : "category"
                  }`}>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Updating...
                    </>
                  ) : (
                    `Update ${
                      formData.parent_category_id ? "Sub Category" : "Category"
                    }`
                  )}
                </button>

                {/* Hidden button for programmatic modal close */}
                <button
                  id="edit-category-dismiss-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditCategory;
